import { useCallback, useRef, useState } from 'react';
import {
    Seat, MealPeriod, ItemMealCategory, MealPlanItemStatus, MealPlanEvaluation,
    SeatMealPlanResult, AllowanceSummaryItem, MealPlanAllowance,
    MealPlanConfig, ConsumedItem,
} from '../interfaces/mealPlan';
import { OrderProduct } from '../interfaces';

// ─── Helper: get current time as "HH:MM" ─────────────────────────────────────

function getCurrentTimeString(): string {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

// Business date for meal allowances. Must be the LOCAL calendar date: toISOString() would give
// the UTC date, which rolls over at a local wall-clock time that is not midnight — in a negative
// UTC offset that lands mid-dinner service and silently hands the guest a second allowance.
function getTodayString(): string {
    return getLocalDateString();
}

// Reservation check-in/check-out dates arrive from the PMS as full ISO datetimes
// (e.g. "2026-06-05T00:00:00"), while "today" is a plain "YYYY-MM-DD". Comparing the
// two directly always fails, so normalize any date-ish string to its date-only part.
function toDateOnly(s?: string): string {
    return (s ?? '').split('T')[0].trim();
}

// Local calendar date as "YYYY-MM-DD". Reservation dates are local calendar dates,
// so check-in/check-out day comparisons must use the local date — not the UTC date
// from toISOString(), which can roll to the next/previous day in non-UTC timezones.
function getLocalDateString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function timeToMinutes(t: string): number {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
}

// ─── Helper: determine meal category from POS category name ──────────────────
// The rules in each meal period directly reference mealCategory values like
// "main_course", "appetizer", etc. We match the POS product category name
// against these rule categories. If no rule references this category, it's "other".

function inferMealCategory(posCategoryName: string, config: MealPlanConfig | null): ItemMealCategory {
    if (!config) return 'other';
    const lower = posCategoryName.toLowerCase();

    // Check if any rule in any period references a category that matches
    for (const period of config.mealPeriods) {
        for (const rule of period.rules) {
            if (rule.mealCategory.toLowerCase() === lower) {
                return rule.mealCategory as ItemMealCategory;
            }
        }
    }

    // Common drink category names
    const drinkKeywords = ['drink', 'beverage', 'juice', 'beer', 'spirit', 'wine', 'cocktail', 'coffee', 'tea', 'soft drink'];
    if (drinkKeywords.some(k => lower.includes(k))) return 'drink';

    return 'other';
}

export function getItemMealCategory(product: OrderProduct, config: MealPlanConfig | null): ItemMealCategory {
    const categoryName = (product as any).categoryName || '';
    if (categoryName) {
        return inferMealCategory(categoryName, config);
    }
    return 'other';
}

export function getItemMealCategoryFromCategoryName(categoryName: string, config: MealPlanConfig | null): ItemMealCategory {
    return inferMealCategory(categoryName, config);
}

// ─── The Hook ─────────────────────────────────────────────────────────────────

export function useMealPlanEngine() {
    // Prefetch cache for consumption status: "roomNumber|guestName|mealPeriod|date" -> boolean
    const consumptionCacheRef = useRef<Record<string, boolean>>({});

    // Cache of fetched meal plan configs by rateId
    const configCacheRef = useRef<Record<string, MealPlanConfig | null>>({});
    const [loadingConfigs, setLoadingConfigs] = useState(false);

    // ─── Fetch meal plan config for a rate ──────────────────────────────────

    const fetchMealPlanConfig = useCallback(async (rateId: string, forceRefresh?: boolean): Promise<MealPlanConfig | null> => {
        if (!forceRefresh && configCacheRef.current[rateId] !== undefined) {
            return configCacheRef.current[rateId];
        }
        try {
            setLoadingConfigs(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/MealPlanConfiguration/by-rate/${rateId}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                configCacheRef.current[rateId] = null;
                return null;
            }
            const data: MealPlanConfig = await response.json();
            configCacheRef.current[rateId] = data;
            return data;
        } catch (error) {
            console.error('[MealPlan] Error fetching config for rateId:', rateId, error);
            configCacheRef.current[rateId] = null;
            return null;
        } finally {
            setLoadingConfigs(false);
        }
    }, []);

    // ─── Determine current meal period from config ──────────────────────────

    const getCurrentMealPeriod = useCallback((config: MealPlanConfig | null): MealPeriod | null => {
        if (!config) return null;
        const now = getCurrentTimeString();
        const nowMin = timeToMinutes(now);

        for (const period of config.mealPeriods) {
            if (!period.isEnabled) continue;
            if (nowMin >= timeToMinutes(period.timeWindowStart) && nowMin <= timeToMinutes(period.timeWindowEnd)) {
                return period.mealPeriod as MealPeriod;
            }
        }
        return null;
    }, []);

    // ─── The period an open bill is priced in ───────────────────────────────
    // A bill keeps the meal period it was opened in. The POS stamps
    // seat.capturedMealPeriod when the table is set up; re-deriving the period from the
    // clock every time the check is rendered re-priced a lunch order as a full charge the
    // moment 14:30 passed, and would have priced it against DINNER's rules by 18:00.
    // The capture stops applying only when the plan says to charge full once the period
    // ends — an unset flag means that toggle was never turned on, i.e. don't charge full.

    const getCapturedMealPeriod = useCallback((
        seat: Seat,
        config: MealPlanConfig | null,
    ): MealPeriod | null => {
        const captured = (seat.capturedMealPeriod || '').toLowerCase();
        if (!captured || !config) return null;

        const periodConfig = config.mealPeriods.find(
            p => p.mealPeriod.toLowerCase() === captured && p.isEnabled
        );
        if (!periodConfig) return null;

        // A capture only speaks for the service it was taken in. Seats survive on a table
        // (an order is not closed just because the day ended), so a breakfast capture must
        // not still be pricing the same seats at lunch. Today's captures apply; yesterday's
        // apply only until the first meal window of today opens, which is what keeps a late
        // dinner that settles after local midnight on its dinner allowance.
        const nowMin = timeToMinutes(getCurrentTimeString());
        if (seat.capturedDate && seat.capturedDate !== getLocalDateString()) {
            const firstWindowStart = config.mealPeriods
                .filter(p => p.isEnabled)
                .reduce((earliest, p) => Math.min(earliest, timeToMinutes(p.timeWindowStart)), Infinity);
            if (nowMin >= firstWindowStart) return null;
        }

        if (periodConfig.chargeFullAfterPeriod === true && nowMin > timeToMinutes(periodConfig.timeWindowEnd)) {
            return null;
        }

        return periodConfig.mealPeriod as MealPeriod;
    }, []);

    // ─── Check if meal period is valid for a guest ──────────────────────────

    const isMealPeriodValidForGuest = useCallback((
        seat: Seat,
        mealPeriod: MealPeriod,
        config: MealPlanConfig | null,
        options?: { periodWasCaptured?: boolean },
    ): { valid: boolean; reason?: string } => {
        if (!config) {
            return { valid: false, reason: 'No meal plan configuration found' };
        }

        const periodConfig = config.mealPeriods.find(p => p.mealPeriod === mealPeriod && p.isEnabled);
        if (!periodConfig) {
            return { valid: false, reason: `${mealPeriod} not included in plan` };
        }

        // Local date so check-in/check-out day matches the PMS calendar date regardless of timezone.
        const today = getLocalDateString();

        // Check-in day rules
        if (toDateOnly(seat.checkInDate) === today) {
            if (config.checkinDayPolicy === 'first_available_meal') {
                // "First available meal" excludes the meals whose window had already closed
                // when the guest arrived. It must not exclude the one they are sitting in:
                // once the POS captured this period for the seat, the guest WAS ordering
                // inside the window, so the bill keeps the allowance however late it settles.
                const nowMin = timeToMinutes(getCurrentTimeString());
                if (!options?.periodWasCaptured && nowMin > timeToMinutes(periodConfig.timeWindowEnd)) {
                    return { valid: false, reason: `Check-in day: ${mealPeriod} period has passed` };
                }
            }
        }

        // Check-out day rules
        if (toDateOnly(seat.checkOutDate) === today) {
            if (config.checkoutDayPolicy === 'breakfast_only' && mealPeriod !== 'breakfast') {
                return { valid: false, reason: 'Check-out day: only breakfast is covered' };
            }
            if (config.checkoutDayPolicy === 'no_meals') {
                return { valid: false, reason: 'Check-out day: no meals covered' };
            }
        }

        return { valid: true };
    }, []);

    // ─── Check for double-dipping (reads from prefetch cache) ─────────────

    const getConsumptionKey = useCallback((roomNumber: string, guestName: string, mealPeriod: string, date: string) => {
        return `${roomNumber}|${guestName}|${mealPeriod.toLowerCase()}|${date}`;
    }, []);

    const hasAlreadyUsedAllowance = useCallback((
        seat: Seat,
        mealPeriod: MealPeriod,
    ): boolean => {
        const today = getTodayString();
        const key = getConsumptionKey(seat.roomNumber || '', seat.guestName || '', mealPeriod, today);
        const result = consumptionCacheRef.current[key] === true;
        console.log(`[MealPlan] hasAlreadyUsedAllowance: key="${key}" result=${result}`, 'cache=', { ...consumptionCacheRef.current });
        return result;
    }, [getConsumptionKey]);

    // ─── Prefetch consumption status for all hotel-guest seats ──────────────

    const prefetchConsumptionStatus = useCallback(async (seats: Seat[], configs?: Record<string, MealPlanConfig | null>) => {
        const today = getTodayString();
        const hotelSeats = seats.filter(s => s.type === 'hotel_guest' && s.roomNumber && s.guestName);

        // Collect all unique meal period names from configs, falling back to defaults
        const periodSet = new Set<string>();
        if (configs) {
            for (const config of Object.values(configs)) {
                if (config) {
                    for (const p of config.mealPeriods) {
                        if (p.isEnabled) periodSet.add(p.mealPeriod.toLowerCase());
                    }
                }
            }
        }
        if (periodSet.size === 0) {
            periodSet.add('breakfast');
            periodSet.add('lunch');
            periodSet.add('dinner');
        }
        const periods = Array.from(periodSet) as MealPeriod[];

        for (const seat of hotelSeats) {
            for (const period of periods) {
                const key = getConsumptionKey(seat.roomNumber || '', seat.guestName || '', period, today);
                // Always re-fetch to avoid stale cache across orders
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/MealConsumption/check/${encodeURIComponent(seat.roomNumber || '')}/${encodeURIComponent(seat.guestName || '')}/${period}/${today}`,
                        { method: 'GET', credentials: 'include' }
                    );
                    if (response.ok) {
                        const data = await response.json();
                        consumptionCacheRef.current[key] = data.alreadyUsed === true;
                        console.log(`[MealPlan] prefetch: ${seat.guestName} | period=${period} | alreadyUsed=${data.alreadyUsed}`);
                    } else {
                        consumptionCacheRef.current[key] = false;
                        console.log(`[MealPlan] prefetch: ${seat.guestName} | period=${period} | response not ok (${response.status})`);
                    }
                } catch {
                    // Fail open: don't block guest
                    consumptionCacheRef.current[key] = false;
                }
            }
        }
    }, [getConsumptionKey]);

    // ─── Get allowance for a seat's current meal period ────────────────────

    const getAllowanceForSeat = useCallback((
        seat: Seat,
        mealPeriod: MealPeriod,
        config: MealPlanConfig | null,
    ): MealPlanAllowance | null => {
        if (!config) return null;

        const periodConfig = config.mealPeriods.find(p => p.mealPeriod === mealPeriod && p.isEnabled);
        if (!periodConfig) return null;

        return {
            mealPeriod,
            includedItems: periodConfig.rules.map(r => ({
                category: r.mealCategory as ItemMealCategory,
                maxQuantity: r.maxQuantity,
            })),
            drinksIncluded: config.drinksIncluded,
            timeWindow: { start: periodConfig.timeWindowStart, end: periodConfig.timeWindowEnd },
        };
    }, []);

    // ─── Auto-optimize: determine which items are included in plan ─────────

    const evaluateSeatItems = useCallback((
        seat: Seat,
        items: (OrderProduct & { categoryName?: string })[],
        config: MealPlanConfig | null,
        mealPeriodOverride?: MealPeriod | null,
    ): SeatMealPlanResult => {
        const warnings: string[] = [];
        const capturedMealPeriod = getCapturedMealPeriod(seat, config);
        let currentMealPeriod = (mealPeriodOverride !== undefined && mealPeriodOverride !== null)
            ? mealPeriodOverride
            : (capturedMealPeriod ?? getCurrentMealPeriod(config));
        let isWithinTimeWindow = currentMealPeriod !== null;

        // Walk-in: everything is chargeable
        if (seat.type === 'walk_in') {
            return {
                seatId: seat.id,
                currentMealPeriod,
                isWithinTimeWindow,
                isAllowanceAlreadyUsed: false,
                evaluations: items.map(item => ({
                    orderProductId: item.id,
                    status: 'none' as MealPlanItemStatus,
                })),
                totalIncluded: 0,
                totalExtra: items.reduce((sum, i) => sum + i.price * i.qty, 0),
                allowanceSummary: [],
                warnings: [],
            };
        }

        // No config for this rate
        if (!config) {
            return {
                seatId: seat.id,
                currentMealPeriod,
                isWithinTimeWindow,
                isAllowanceAlreadyUsed: false,
                evaluations: items.map(item => ({
                    orderProductId: item.id,
                    status: 'extra' as MealPlanItemStatus,
                    reason: 'No meal plan configuration for this rate',
                })),
                totalIncluded: 0,
                totalExtra: items.reduce((sum, i) => sum + i.price * i.qty, 0),
                allowanceSummary: [],
                warnings: ['No meal plan configuration found for this rate plan'],
            };
        }

        // Outside time window
        if (!isWithinTimeWindow) {
            // Find the most recently ended meal period to check its chargeFullAfterPeriod flag.
            // If that period's flag is false, honor meal plan inclusions using that period.
            let fallbackToIncluded = false;
            if (config) {
                const nowMin = timeToMinutes(getCurrentTimeString());
                let mostRecentPeriod: typeof config.mealPeriods[0] | null = null;
                let smallestGap = Infinity;
                for (const p of config.mealPeriods) {
                    if (!p.isEnabled) continue;
                    const endMin = timeToMinutes(p.timeWindowEnd);
                    const gap = nowMin - endMin;
                    if (gap > 0 && gap < smallestGap) {
                        smallestGap = gap;
                        mostRecentPeriod = p;
                    }
                }
                // An unset flag is the toggle in its default (off) position, so only an
                // explicit true charges the full price after the window closes.
                if (mostRecentPeriod && mostRecentPeriod.chargeFullAfterPeriod !== true) {
                    currentMealPeriod = mostRecentPeriod.mealPeriod as MealPeriod;
                    isWithinTimeWindow = true;
                    fallbackToIncluded = true;
                }
            }

            if (!fallbackToIncluded) {
                warnings.push('Outside meal period time window. All items will be charged as extra.');
                let incTotal = 0;
                let extTotal = 0;
                const evals = items.map(item => {
                    if ((item as any).mealPlanOverride?.waived) {
                        incTotal += item.price * item.qty;
                        return { orderProductId: item.id, status: 'included' as MealPlanItemStatus };
                    }
                    extTotal += item.price * item.qty;
                    return { orderProductId: item.id, status: 'extra' as MealPlanItemStatus, reason: 'Outside meal period time window' };
                });
                return {
                    seatId: seat.id,
                    currentMealPeriod: null,
                    isWithinTimeWindow: false,
                    isAllowanceAlreadyUsed: false,
                    evaluations: evals,
                    totalIncluded: incTotal,
                    totalExtra: extTotal,
                    allowanceSummary: [],
                    warnings,
                };
            }
        }

        // Check meal period validity
        const periodCheck = isMealPeriodValidForGuest(seat, currentMealPeriod, config, {
            periodWasCaptured: capturedMealPeriod !== null && capturedMealPeriod === currentMealPeriod,
        });
        if (!periodCheck.valid) {
            warnings.push(periodCheck.reason || 'Meal period not covered');
            let incTotal = 0;
            let extTotal = 0;
            const evals = items.map(item => {
                if ((item as any).mealPlanOverride?.waived) {
                    incTotal += item.price * item.qty;
                    return { orderProductId: item.id, status: 'included' as MealPlanItemStatus };
                }
                extTotal += item.price * item.qty;
                return { orderProductId: item.id, status: 'extra' as MealPlanItemStatus, reason: periodCheck.reason };
            });
            return {
                seatId: seat.id,
                currentMealPeriod,
                isWithinTimeWindow: true,
                isAllowanceAlreadyUsed: false,
                evaluations: evals,
                totalIncluded: incTotal,
                totalExtra: extTotal,
                allowanceSummary: [],
                warnings,
            };
        }

        // Double-dipping check
        if (config.doubleDipPrevention) {
            const alreadyUsed = hasAlreadyUsedAllowance(seat, currentMealPeriod);
            if (alreadyUsed) {
                warnings.push(`${currentMealPeriod} allowance already used for ${seat.guestName} (Room ${seat.roomNumber}).`);
                let incTotal = 0;
                let extTotal = 0;
                const evals = items.map(item => {
                    if ((item as any).mealPlanOverride?.waived) {
                        incTotal += item.price * item.qty;
                        return { orderProductId: item.id, status: 'included' as MealPlanItemStatus };
                    }
                    extTotal += item.price * item.qty;
                    return { orderProductId: item.id, status: 'extra' as MealPlanItemStatus, reason: 'Allowance already used this meal period' };
                });
                return {
                    seatId: seat.id,
                    currentMealPeriod,
                    isWithinTimeWindow: true,
                    isAllowanceAlreadyUsed: true,
                    evaluations: evals,
                    totalIncluded: incTotal,
                    totalExtra: extTotal,
                    allowanceSummary: [],
                    warnings,
                };
            }
        }

        // Get the allowance
        const allowance = getAllowanceForSeat(seat, currentMealPeriod, config);
        if (!allowance) {
            return {
                seatId: seat.id,
                currentMealPeriod,
                isWithinTimeWindow: true,
                isAllowanceAlreadyUsed: false,
                evaluations: items.map(item => ({
                    orderProductId: item.id,
                    status: 'extra' as MealPlanItemStatus,
                    reason: 'No allowance found',
                })),
                totalIncluded: 0,
                totalExtra: items.reduce((sum, i) => sum + i.price * i.qty, 0),
                allowanceSummary: [],
                warnings: ['No allowance configuration found for this meal period'],
            };
        }

        // ─── Auto-optimization: include most/least expensive qualifying items ──

        interface CategorizedItem {
            item: OrderProduct & { categoryName?: string };
            mealCategory: ItemMealCategory;
            totalPrice: number;
        }

        const categorizedItems: CategorizedItem[] = items.map(item => ({
            item,
            mealCategory: item.categoryName
                ? getItemMealCategoryFromCategoryName(item.categoryName, config)
                : 'other',
            totalPrice: item.price * item.qty,
        }));

        // Track remaining allowance per category
        const remainingAllowance: Record<string, number> = {};
        for (const rule of allowance.includedItems) {
            remainingAllowance[rule.category] = (remainingAllowance[rule.category] || 0) + rule.maxQuantity;
        }

        // Sort by price based on optimization direction
        const isGuestFavorable = config.optimizationDirection === 'guest_favorable';
        const sortedItems = [...categorizedItems].sort((a, b) =>
            isGuestFavorable ? b.totalPrice - a.totalPrice : a.totalPrice - b.totalPrice
        );

        const evaluations: MealPlanEvaluation[] = [];
        let totalIncluded = 0;
        let totalExtra = 0;
        const assignedStatus: Record<string, MealPlanItemStatus> = {};
        const assignedReasons: Record<string, string> = {};

        // First pass: handle manually overridden (waived) items
        for (const ci of sortedItems) {
            const { item, totalPrice } = ci;
            if ((item as any).mealPlanOverride?.waived) {
                assignedStatus[item.id] = 'included';
                totalIncluded += totalPrice;
            }
        }

        // Second pass: allocate remaining items normally
        for (const ci of sortedItems) {
            const { item, mealCategory, totalPrice } = ci;

            // Skip already-handled overrides
            if (assignedStatus[item.id]) continue;

            // Drinks check
            if (mealCategory === 'drink' && !config.drinksIncluded) {
                assignedStatus[item.id] = 'extra';
                assignedReasons[item.id] = 'Drinks not included in meal plan';
                totalExtra += totalPrice;
                continue;
            }

            // "other" category - not covered
            if (mealCategory === 'other') {
                assignedStatus[item.id] = 'extra';
                assignedReasons[item.id] = 'Item category not covered by meal plan';
                totalExtra += totalPrice;
                continue;
            }

            // Check remaining allowance
            if (remainingAllowance[mealCategory] !== undefined && remainingAllowance[mealCategory] > 0) {
                assignedStatus[item.id] = 'included';
                remainingAllowance[mealCategory] -= item.qty;
                totalIncluded += totalPrice;
            } else {
                assignedStatus[item.id] = 'extra';
                assignedReasons[item.id] = 'Exceeds meal plan allowance';
                totalExtra += totalPrice;
            }
        }

        // Build evaluations in original item order
        for (const item of items) {
            evaluations.push({
                orderProductId: item.id,
                status: assignedStatus[item.id] || 'extra',
                reason: assignedReasons[item.id],
            });
        }

        // Build allowance summary
        const allowanceSummary: AllowanceSummaryItem[] = [];
        for (const rule of allowance.includedItems) {
            if (rule.category === 'buffet') continue; // unlimited
            const usedCount = categorizedItems.filter(ci =>
                ci.mealCategory === rule.category &&
                assignedStatus[ci.item.id] === 'included'
            ).length;
            const labelMap: Record<string, string> = {
                main_course: 'Main Course',
                menu_choice: 'Menu Choice',
                appetizer: 'Appetizer',
                dessert: 'Dessert',
                children: 'Children',
            };
            allowanceSummary.push({
                category: rule.category,
                label: labelMap[rule.category] || rule.category,
                used: usedCount,
                max: rule.maxQuantity,
            });
        }

        // Drinks warning
        const hasDrinks = categorizedItems.some(ci => ci.mealCategory === 'drink');
        if (hasDrinks && !config.drinksIncluded) {
            warnings.push('Drinks are not included in the meal plan');
        }

        return {
            seatId: seat.id,
            currentMealPeriod,
            isWithinTimeWindow: true,
            isAllowanceAlreadyUsed: false,
            evaluations,
            totalIncluded,
            totalExtra,
            allowanceSummary,
            warnings,
        };
    }, [getCurrentMealPeriod, getCapturedMealPeriod, isMealPeriodValidForGuest, hasAlreadyUsedAllowance, getAllowanceForSeat]);

    // ─── Record consumption (persists to API) ──────────────────────────────

    const recordConsumption = useCallback(async (
        seat: Seat,
        mealPeriod: MealPeriod,
        items: ConsumedItem[],
        orderId?: string,
    ): Promise<boolean> => {
        // Key the record to the day the seat was opened, so an order that stays open across
        // midnight still burns the allowance of the day the guest actually ate on.
        const date = seat.capturedDate || getTodayString();
        const normalizedPeriod = mealPeriod.toLowerCase();
        const payload = {
            seatId: seat.id,
            guestName: seat.guestName || '',
            roomNumber: seat.roomNumber || '',
            reservationNumber: seat.reservationNumber || '',
            orderId: orderId || '',
            mealPeriod: normalizedPeriod,
            date,
            itemsConsumed: items,
            isAllowanceUsed: true,
        };

        // Update local cache immediately — this device must not re-grant the allowance even if
        // the POST below fails.
        const key = getConsumptionKey(seat.roomNumber || '', seat.guestName || '', normalizedPeriod, date);
        consumptionCacheRef.current[key] = true;
        console.log(`[MealPlan] recordConsumption: ${seat.guestName} | period=${normalizedPeriod} | key=${key}`);

        // The response was previously ignored, so a rejected POST left the cache claiming the
        // allowance was burned while nothing reached the database — every other device (and this
        // one after a refresh) then handed out the same allowance again. Retry once, and shout
        // when it still fails; the API also records this on order close, so a failure here is a
        // fallback loss rather than a guaranteed double-dip.
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/MealConsumption`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (response.ok) return true;
                console.error(`[MealPlan] recordConsumption rejected (${response.status}) for ${seat.guestName} | period=${normalizedPeriod}`);
            } catch (error) {
                console.error('[MealPlan] Error recording consumption:', error);
            }
        }
        return false;
    }, [getConsumptionKey]);

    // ─── Get meal plan status for a single item ────────────────────────────

    const getItemStatus = useCallback((
        seat: Seat,
        item: OrderProduct & { categoryName?: string },
        allSeatItems: (OrderProduct & { categoryName?: string })[],
        config: MealPlanConfig | null,
        mealPeriodOverride?: MealPeriod | null,
    ): MealPlanItemStatus => {
        const result = evaluateSeatItems(seat, allSeatItems, config, mealPeriodOverride ?? undefined);
        const evaluation = result.evaluations.find(e => e.orderProductId === item.id);
        return evaluation?.status ?? 'none';
    }, [evaluateSeatItems]);

    return {
        fetchMealPlanConfig,
        loadingConfigs,
        prefetchConsumptionStatus,
        getCurrentMealPeriod,
        getCapturedMealPeriod,
        isMealPeriodValidForGuest,
        hasAlreadyUsedAllowance,
        getAllowanceForSeat,
        evaluateSeatItems,
        recordConsumption,
        getItemStatus,
        getItemMealCategory,
        getItemMealCategoryFromCategoryName,
    };
}

// Re-export for direct import
export type { ConsumedItem } from '../interfaces/mealPlan';
